import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
// The PNG's pixels per pixel of the SVG (10 a module): 30 a module, some
// 1200 pixels across.
const PNG_SCALE = 3;
const t = key => app.translator.trans(`champis-exhibitions.forum.qr_code_modal.${key}`);

// The name the server gives the SVG download (Filename::contentDisposition()).
function filename(disposition) {
  const encoded = disposition?.match(/filename\*=UTF-8''([^;]+)/)?.[1];
  return encoded ? decodeURIComponent(encoded) : 'QR code.svg';
}
function load(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });
}

/**
 * A QR code to the exhibition's public page (ExpoPage), champis.net's logo
 * in its middle - for a poster, a flyer, a table at the exhibition. Drawn
 * by the server (ShowExhibitionQrCodeController); downloaded as is (SVG,
 * sharp at any size, for print) or as a PNG drawn from it here.
 */
export default class ExhibitionQrCodeModal extends Modal {
  downloading = false;
  className() {
    return 'Modal--small ChampisQrCodeModal';
  }
  title() {
    return t('title');
  }
  svgUrl(download) {
    if (download === void 0) {
      download = false;
    }
    return `${app.forum.attribute('apiUrl')}/champis-exhibitions/${this.attrs.exhibition.id()}/qrcode.svg${download ? '?download=1' : ''}`;
  }
  content() {
    const publicUrl = app.forum.attribute('baseOrigin') + app.route('expo', {
      id: this.attrs.exhibition.id()
    });
    return m("div", {
      className: "Modal-body"
    }, m("img", {
      className: "ChampisQrCodeModal-image",
      src: this.svgUrl(),
      alt: t('image_alt')
    }), m("p", {
      className: "ChampisQrCodeModal-url"
    }, m("a", {
      href: publicUrl,
      target: "_blank",
      rel: "noopener noreferrer"
    }, publicUrl)), m("div", {
      className: "ChampisQrCodeModal-actions"
    }, m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-download",
      loading: this.downloading,
      onclick: () => this.downloadPng()
    }, t('download_png_button')), m("a", {
      className: "Button",
      href: this.svgUrl(true),
      download: true
    }, m("i", {
      className: "icon fas fa-download Button-icon",
      "aria-hidden": "true"
    }), m("span", {
      className: "Button-label"
    }, t('download_svg_button')))), m("p", {
      className: "ChampisQrCodeModal-hint"
    }, t('formats_hint')));
  }
  async downloadPng() {
    this.downloading = true;
    try {
      const response = await fetch(this.svgUrl(true), {
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error(`${response.status}`);
      const name = filename(response.headers.get('Content-Disposition')).replace(/\.svg$/, '.png');
      const svg = URL.createObjectURL(await response.blob());
      try {
        const image = await load(svg);
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth * PNG_SCALE;
        canvas.height = image.naturalHeight * PNG_SCALE;
        canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
        const png = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        if (!png) throw new Error('toBlob');
        const link = document.createElement('a');
        link.href = URL.createObjectURL(png);
        link.download = name;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      } finally {
        URL.revokeObjectURL(svg);
      }
    } catch {
      app.alerts.show({
        type: 'error'
      }, t('download_error'));
    } finally {
      this.downloading = false;
      m.redraw();
    }
  }
}
flarum.reg.add('champis-exhibitions', 'forum/components/ExhibitionQrCodeModal', ExhibitionQrCodeModal);